import { faTrash } from "@fortawesome/free-solid-svg-icons/faTrash";
import { faUserCircle } from "@fortawesome/free-solid-svg-icons/faUserCircle";
import { Tooltip } from "../../../utils/local-antd";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";

import { AtiraIcon } from "../../../components/AtiraIcon";
import { AtiraSelect } from "../../../components/AtiraSelect";
import { Button } from "../../../components/Button";
import { Flex } from "../../../components/Flex";
import { FontSize, Text } from "../../../components/Text";
import { WarningModal } from "../../../components/WarningModal";
import { ASSIGNABLE_TEAM_ROLES } from "../../../model/permissions/team-role.config";
import { TeamRole } from "../../../model/permissions/types/TeamRole.enum";
import { User } from "../../../model/user/User";
import { permissionActions } from "../../../redux/permission/permission.slice";
import { useAppDispatch, useAppSelector } from "../../../redux/store";
import { userSliceSelectors } from "../../../redux/user/user.selector";
import { TestIDs } from "../../../tests/types/testIds";
import { Rounded } from "../../../theme/Rounded";
import { Spacing } from "../../../theme/Spacing";
import { AtiraToast } from "../../../utils/AtiraToast";
import { standardDate } from "../../../utils/Date";
import { usePermissionsContext } from "../permissions-context";
import { ImageCreateEdit } from "../../../components/ImageCreateEdit";
import { getTeamRole, isCustomizedFromRole } from "../utils/permissionUtils";
import {
  CustomizedPermissionsBadge,
  TeamRoleBadge,
  TeamRoleLabel,
} from "./TeamRoleBadge";

const Container = styled(Flex)`
  align-items: center;
  justify-content: space-between;
  padding: ${Spacing.s};
  border-radius: ${Rounded.md};
  width: 100%;
`;

const InfoWrapper = styled(Flex)`
  align-items: center;
  gap: ${Spacing.m};
`;

const ImageWrapper = styled(Flex)`
  height: 3rem;
  width: 3rem;
  border-radius: ${Rounded.circle};
  overflow: hidden;
  border: ${({ theme }) => `2px solid ${theme.main}`};
`;

const IconWrapper = styled.div`
  .svg-inline--fa {
    width: 100%;
    height: 100%;
    color: ${({ theme }) => theme.main};
    font-size: 5rem;
  }
`;

const ActionsWrapper = styled(Flex)`
  align-items: center;
  gap: ${Spacing.l};
  margin-inline-end: ${Spacing.m};
`;

const RoleSelectWrapper = styled.div`
  min-width: 9rem;
`;

const StyledButton = styled(Button)`
  margin: 0;
  padding: 0;
  background-color: ${({ theme }) => theme.transparent};
  color: ${({ theme }) => theme.main};
  font-size: ${FontSize.l};
`;

interface MemberRowProps {
  member?: User;
}

export const MemberRow: React.FC<MemberRowProps> = ({ member }) => {
  const [nextRole, setNextRole] = useState<TeamRole>();
  const [roleLoading, setRoleLoading] = useState(false);

  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { setRemoveMemberModalVisible, setCurrentMember } =
    usePermissionsContext();

  const user = useAppSelector(userSliceSelectors.selectLoggedInUser)!;

  const memberRole = getTeamRole(member);

  const onRemoveMember = () => {
    setRemoveMemberModalVisible(true);
    setCurrentMember(member!);
  };

  const onConfirmRoleChange = async () => {
    try {
      setRoleLoading(true);

      await dispatch(
        permissionActions.updateMemberRole({
          userId: user._id,
          memberId: member!._id,
          orgRole: nextRole!,
        }),
      ).unwrap();
      await dispatch(
        permissionActions.getMembers({ userId: user._id }),
      ).unwrap();

      AtiraToast.success(t("permissions.team-role.change.success"));
      setNextRole(undefined);
    } catch (e: any) {
      console.error(e);
      AtiraToast.apiError(e);
    } finally {
      setRoleLoading(false);
    }
  };

  return (
    <Container>
      <InfoWrapper>
        <ImageWrapper>
          {member?.picture ? (
            <ImageCreateEdit src={member.picture} />
          ) : (
            <IconWrapper>
              <AtiraIcon icon={faUserCircle} />
            </IconWrapper>
          )}
        </ImageWrapper>

        <Flex justifyContent="center" alignItems="center" gap="s">
          <Text>{member?.name}</Text>
          <Text fontSize="s">({member?.email})</Text>

          {memberRole ? (
            <TeamRoleBadge
              role={memberRole}
              dataTestId={TestIDs.permissions.teamRole.memberBadge(member!._id)}
            />
          ) : null}
          {memberRole &&
          isCustomizedFromRole(memberRole, member?.permissions?.permissions) ? (
            <CustomizedPermissionsBadge
              dataTestId={TestIDs.permissions.teamRole.memberCustomized(
                member!._id,
              )}
            />
          ) : null}

          {member?.inviteInfo?.respondedToAt ? (
            <Text fontSize="s">
              {t("permissions.member.member-since", {
                date: standardDate(member.inviteInfo.respondedToAt, user),
              })}
            </Text>
          ) : null}
        </Flex>
      </InfoWrapper>

      <ActionsWrapper>
        <RoleSelectWrapper>
          <AtiraSelect
            value={memberRole}
            options={ASSIGNABLE_TEAM_ROLES.map(({ id }) => ({
              label: <TeamRoleLabel role={id} />,
              value: id,
              "data-testid": TestIDs.permissions.teamRole.option(id),
            }))}
            onChange={(role) => {
              if (role !== memberRole) {
                setNextRole(role as TeamRole);
              }
            }}
            className={TestIDs.permissions.teamRole.memberSelect(member!._id)}
          />
        </RoleSelectWrapper>

        <Tooltip title={t("common.remove")}>
          <StyledButton onClick={onRemoveMember} icon={faTrash} />
        </Tooltip>
      </ActionsWrapper>

      <WarningModal
        isOpen={Boolean(nextRole)}
        title={t("permissions.team-role.change.modal.title", {
          name: member?.name,
          role: nextRole ? t(`permissions.team-role.${nextRole}`) : "",
        })}
        description={t("permissions.team-role.change.modal.description")}
        onConfirm={onConfirmRoleChange}
        onClose={() => setNextRole(undefined)}
        loading={roleLoading}
        confirmButtonText={t("common.confirm")}
        confirmButtonDataTestId={TestIDs.permissions.teamRole.changeConfirm}
        denyButtonText={t("common.cancel")}
      />
    </Container>
  );
};
